<script setup>
import { userAuthState } from '@/@core/auth'
import DashboardTicketTypeCard from '@/@core/components/feedback/DashboardTicketTypeCard.vue'
import DashboardUnitOverviewCard from '@/@core/components/feedback/DashboardUnitOverviewCard.vue'
import UnitWiseExperience from '@/@core/components/feedback/UnitWiseExperience.vue'
import UnitWiseFeedback from '@/@core/components/feedback/UnitWiseFeedback.vue'
import UnitWiseFeedbackTicketAvgTat from '@/@core/components/feedback/UnitWiseFeedbackTicketAvgTat.vue'
import UnitWiseNps from '@/@core/components/feedback/UnitWiseNps.vue'
import UnitWiseTicketsByStatus from '@/@core/components/feedback/UnitWiseTicketsByStatus.vue'
import UnitWiseTicketsByType from '@/@core/components/feedback/UnitWiseTicketsByType.vue'
import { endOfMonth, startOfMonth } from '@/@core/utils/formatters'
import { can } from '@/@layouts/plugins/casl'
import { cookieRef } from '@/@layouts/stores/config'
import TicketTable from '@/views/feedback/ticket-management/TicketTable.vue'
import { computed } from 'vue'

definePage({
  meta: {
    name: 'dashboard-unit',
    subject: 'feedback.menu',
    action: 'dashboard_unit',
  },
})

const auth = userAuthState()
const canAccessUnit = can('read', 'master.unit')

// Master data
const { data: formTypes } = await useApi(
  createUrl('masters/form-types', {
    query: {
      select: ['id', 'name'],
    },
  }),
)

const { data: unitsData } = await useApi(
  createUrl('/masters/units', {
    query: {
      paged: 1,
      select: ['id', 'name'],
    },
  }),
  {
    beforeFetch: ({ cancel }) => {
      if (!canAccessUnit) cancel()
    },
  },
)

const unitId = ref(cookieRef('selectedUnit') ?? null)

// Date utilities
const addDays = (date, days) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)

  return result
}

const addMonths = (date, months) => {
  const result = new Date(date)

  result.setMonth(result.getMonth() + months)

  return result
}

const customDateFormat = date => {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}

const formatFloat = number => {
  if (Number(number) === number && number % 1 !== 0)
    return number.toFixed(2)

  return number
}

// Date range state
const quickDateRangeOptions = [
  {
    title: 'Today',
    value: `${customDateFormat(new Date())}`,
  },
  {
    title: 'Yesterday',
    value: `${customDateFormat(addDays(new Date(), -1))}`,
  },
  {
    title: 'Last 7 days',
    value: `${customDateFormat(addDays(new Date(), -6))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last 30 days',
    value: `${customDateFormat(addDays(new Date(), -30))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'This month',
    value: `${customDateFormat(startOfMonth(new Date()))} to ${customDateFormat(new Date())}`,
  },
  {
    title: 'Last month',
    value: `${customDateFormat(startOfMonth(addMonths(new Date(), -1)))} to ${customDateFormat(endOfMonth(addMonths(new Date(), -1)))}`,
  },
  {
    title: 'Custom',
    value: null,
  },
]

const quickDateRange = ref('Last 30 days')

const setQuickDateRange = value => {
  dateRange.value = value?.value
}

const dateRange = ref()

const fromDate = ref(customDateFormat(addDays(new Date(), -30)))
const toDate = ref(customDateFormat(new Date()))

const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  enableTime: false,
  enableSeconds: false,
  noCalendar: false,
  altInput: true,
  altFormat: 'F j, Y',
  altInputClass: 'form-control',
  altInputPlaceholder: 'Select date',
  inline: false,
  static: false,
  appendTo: document.body,
  clickOpens: true,
  onChange: (selectedDates, dateStr, instance) => {
    fromDate.value = customDateFormat(selectedDates[0])
    if (selectedDates[1])
      toDate.value = customDateFormat(selectedDates[1])
    else
      toDate.value = customDateFormat(selectedDates[0])

    dateRange.value = dateStr
  },
  defaultDate: [customDateFormat(addDays(new Date(), -30)), customDateFormat(new Date())],
})

watch(dateRange, value => {
  if (value) {
    quickDateRange.value = quickDateRangeOptions.find(option => option.value === value)?.title || 'Custom'
  }
}, { immediate: true })

const dateRangeValue = computed(() => {
  return [fromDate.value, toDate.value]
})

// API calls
const { data: totalFeedbackData } = useApi(
  createUrl('/dashboard/feedbacks/total-count', {
    query: {
      search: {
        created_at: dateRangeValue,
        unit_id: unitId,
      },
    },
  }),
)

const { data: totalTicketTypeData } = useApi(
  createUrl('/dashboard/tickets/ticket-type', {
    query: {
      search: {
        created_at: dateRangeValue,
        unit_id: unitId,
      },
    },
  }),
)

const { data: departmentPSatData } = useApi(
  createUrl('/dashboard/feedbacks/p-sat/department', {
    query: {
      search: {
        created_at: dateRangeValue,
        unit_id: unitId,
      },
    },
  }),
)

// P-Sat computed
const pSatData = computed(() => {
  if (!departmentPSatData.value) return null

  const data = departmentPSatData.value.reduce(
    (acc, obj) => {
      acc.overall_p_sat += obj.overall_p_sat || 0
      acc.p_sat_percentage += obj.p_sat_percentage || 0

      return acc
    },
    { overall_p_sat: 0, p_sat_percentage: 0 },
  )

  const count = departmentPSatData.value?.length > 0 ? departmentPSatData.value.length : 1

  return {
    overall_p_sat: formatFloat(data?.overall_p_sat / count) ?? null,
    p_sat_percentage: formatFloat(data?.p_sat_percentage / count) ?? 0,
  }
})

// Ticket table dialog
const ticketTableFilter = ref(null)
const isTicketTableDialogVisible = ref(false)

const onUpdateTicketTableFilter = data => {
  ticketTableFilter.value = {
    created_at: [fromDate.value, toDate.value],
    unit_id: unitId.value,
    status: [],
    ticket_type: ['COMPLAINT', 'COMPLIMENT', 'SUGGESTION', 'SPECIAL_REQUEST'],
    ...data,
  }
  isTicketTableDialogVisible.value = true
}

watch(() => isTicketTableDialogVisible.value, value => {
  if (value === false) ticketTableFilter.value = null
})
</script>

<template>
  <div>
    <div class="text-h4 mb-6">
      Unit Dashboard
    </div>

    <!-- Filter Card -->
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            md="6"
            sm="6"
          >
            <AppSelect
              v-model="quickDateRange"
              label="Quick Range"
              return-object
              :item-title="item => item.title"
              :item-value="item => item.title"
              :items="quickDateRangeOptions"
              @update:model-value="setQuickDateRange"
            />
          </VCol>
          <VCol
            cols="12"
            md="6"
            sm="6"
          >
            <AppDateTimePicker
              v-model="dateRange"
              label="Range"
              placeholder="Select date"
              :config="dateRangeOptions"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Analytics Cards -->
    <VRow class="match-height">
      <!-- Feedback Overview + P-Sat -->
      <VCol
        cols="12"
        md="8"
        style="animation-delay: 0ms;"
      >
        <DashboardUnitOverviewCard
          :feedback-data="totalFeedbackData"
          :p-sat-data="pSatData"
        />
      </VCol>

      <!-- Tickets By Type -->
      <VCol
        cols="12"
        md="4"
        style="animation-delay: 70ms;"
      >
        <DashboardTicketTypeCard :data="totalTicketTypeData" />
      </VCol>

      <!-- UnitWise Chart Widgets -->
      <VCol
        v-if="can('view', 'dashboard.widget.unit_wise_feedbacks', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + fromDate + toDate">
          <UnitWiseFeedback
            :local-form-types="formTypes"
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'dashboard.widget.unit_wise_tickets_by_type', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + fromDate + toDate">
          <UnitWiseTicketsByType
            :local-form-types="formTypes"
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'dashboard.widget.unit_wise_tickets_by_status', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + fromDate + toDate">
          <UnitWiseTicketsByStatus
            :local-form-types="formTypes"
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'dashboard.widget.unit_wise_feedback_ticket_avg_tat', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + fromDate + toDate">
          <UnitWiseFeedbackTicketAvgTat
            :local-form-types="formTypes"
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'dashboard.widget.unit_wise_nps', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + fromDate + toDate">
          <UnitWiseNps
            :local-form-types="formTypes"
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
          />
        </AppLazy>
      </VCol>

      <VCol
        v-if="can('view', 'dashboard.widget.unit_wise_experience', 'feedback')"
        cols="12"
      >
        <AppLazy :key="unitId + fromDate + toDate">
          <UnitWiseExperience
            :local-form-types="formTypes"
            :from="fromDate"
            :to="toDate"
            :unit-id="unitId"
          />
        </AppLazy>
      </VCol>
    </VRow>
  </div>

  <!-- Ticket Table Dialog -->
  <VDialog v-model="isTicketTableDialogVisible">
    <DialogCloseBtn
      size="small"
      @click="() => (isTicketTableDialogVisible = !isTicketTableDialogVisible)"
    />

    <VCard>
      <VCardText>
        <TicketTable
          ref="refTicketTable"
          page="ticket-management"
          :ticket-filters="ticketTableFilter"
          :action-visible="false"
          :style="{
            cardVariant: 'flat',
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
